import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { Point2D as Point2DInterface } from '@paragon/contract-library';
import { DistanceField, OptionalTooltip } from '@paragon/material';

import { movePointInDirection } from '../../../../../library/layout-geometry/directions';
import {
  applyLayoutPointToTargetWithoutAdvancing,
  handlePickedPointInLayoutMode,
} from '../../../../../store/actions/layout/layoutPickingActions';
import { getForcedPickingObjectInfo, getPickingObjectInfo } from '../../../../../store/selectors/pickingSelectors';
import {
  applicationOnPickingLayoutPoint,
  clearPickingObject,
  LayoutPointPickingTarget,
} from '../../../../../store/slices/applicationSlice';
import { PointMovementPopover } from '../../../../controls/PointMovementPopover';
import { useAppDispatch, useAppSelector } from '../../../../hooks';

function Container(props: PropsWithChildren<{ label: string }>) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ marginBottom: 2 }}>
        {props.label}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        {props.children}
      </Stack>
    </Box>
  );
}

interface EscapeModeSettings {
  x?: number;
  y?: number;

  startCoordinate: 'x' | 'y';
}

export function LayoutPointPickerField(props: {
  label: string;
  value?: Point2DInterface;

  disabled?: boolean;

  /**
   * Disables this control and displays both point fields in a <waiting> state. Use at your discretion when there are
   *   multiple fields and you expect forced picking to facilitate a smooth jump between them.
   */
  pending?: boolean;

  target: LayoutPointPickingTarget;

  pickButtonVariant?: 'text' | 'contained' | 'outlined';

  /** If provided, called instead of the default `clearPickingObject()` when the user cancels picking for this field. */
  onCancel?: () => void;

  /**
   * If true, the pick button stays enabled even while a different picker is active, allowing the user to switch the
   *   active picker between fields. THIS IS NOT SAFE for many picking modes unless they are known to handle edge cases
   *   that could arise (currently only bearing envelope insertion has been tested); carefully review before using.
   */
  allowSwitchWhilePicking?: boolean;
}) {
  const dispatch = useAppDispatch();

  const pickingObjectInfo = useAppSelector(getPickingObjectInfo);
  const forcedPickingObjectInfo = useAppSelector(getForcedPickingObjectInfo);

  const isPickingActive = pickingObjectInfo?.type === 'pickingLayoutPoint';

  const isActivePickingForced = isPickingActive && Boolean(forcedPickingObjectInfo);
  const isThisControlPicking = isPickingActive && pickingObjectInfo.target === props.target;

  const [escapeMode, setEscapeMode] = useState<EscapeModeSettings | null>(null);

  // ensure control doesn't get stuck in escape mode when this control is no longer the active picking target
  if (escapeMode && !isThisControlPicking) {
    setEscapeMode(null);
  }

  const [movementAnchor, setMovementAnchor] = useState<HTMLElement | null>(null);

  const canMove = props.value != null && !props.disabled && !props.pending && !isThisControlPicking;

  // ensure the movement popover closes if the point can no longer be moved (eg. this control started picking)
  if (movementAnchor && !canMove) {
    setMovementAnchor(null);
  }

  function cancelPicking() {
    if (props.onCancel) props.onCancel();
    else dispatch(clearPickingObject());
  }

  const pickButton = (
    <Button
      variant={isThisControlPicking ? 'contained' : (props.pickButtonVariant ?? 'outlined')}
      disabled={
        props.disabled || props.pending || (isPickingActive && !isThisControlPicking && !props.allowSwitchWhilePicking)
      }
      onClick={() => {
        if (isThisControlPicking) {
          cancelPicking();
        } else {
          dispatch(applicationOnPickingLayoutPoint({ target: props.target }));
        }
      }}
    >
      {isThisControlPicking ? 'Picking' : 'Pick'}
    </Button>
  );

  const moveButton = (
    <TooltipIconButton
      title="Move point"
      disabled={!canMove}
      onClick={(event) => setMovementAnchor(event.currentTarget)}
    >
      <OpenWithIcon fontSize="small" />
    </TooltipIconButton>
  );

  /**
   * Pending mode - when requested, for when this field is waiting for another related item to finish being picked
   *   before it becomes active.
   */
  if (props.pending) {
    return (
      <Container label={props.label}>
        <TextField size="small" variant="outlined" disabled label="X Coordinate" value="<waiting>" />
        <TextField size="small" variant="outlined" disabled label="Y Coordinate" value="<waiting>" />

        {moveButton}
        {pickButton}
      </Container>
    );
  }

  /**
   * Escape mode - for when the user desires to manually input a point that they are currently being forced to pick
   *   (during which there is no define button). Triggered when one of the coordinate fields is focused during forced
   *   picking.
   */
  if (isThisControlPicking && escapeMode) {
    return (
      <EscapeModeControls
        label={props.label}
        disabled={props.disabled}
        mode={escapeMode}
        onChange={setEscapeMode}
        onEscapeTriggered={async (point) => {
          await dispatch(handlePickedPointInLayoutMode(point));
          setEscapeMode(null);
        }}
        onEscapeCancelled={() => {
          setEscapeMode(null);
          cancelPicking();
        }}
      />
    );
  }

  /**
   * Picking mode - when the user has manually triggered a picking operation or when this field is currently the
   *   "forced" picking target (ie. for the defining point of an insertion object).
   */
  if (isThisControlPicking) {
    return (
      <Container label={props.label}>
        <TextField
          size="small"
          variant="outlined"
          disabled={props.disabled || !isActivePickingForced}
          label="X Coordinate"
          value="<picking>"
          onFocus={() => {
            if (isActivePickingForced) {
              setEscapeMode({
                startCoordinate: 'x',
                x: props.value?.x,
                y: props.value?.y,
              });
            } else {
              dispatch(clearPickingObject());
            }
          }}
        />

        <TextField
          size="small"
          variant="outlined"
          disabled={props.disabled || !isActivePickingForced}
          label="Y Coordinate"
          value="<picking>"
          onFocus={() => {
            if (isActivePickingForced) {
              setEscapeMode({
                startCoordinate: 'y',
                x: props.value?.x,
                y: props.value?.y,
              });
            } else {
              dispatch(clearPickingObject());
            }
          }}
        />

        {moveButton}

        <OptionalTooltip
          visible={!isActivePickingForced}
          disableInteractive
          title="Currently picking this point. Click this button again to cancel."
        >
          <span>{pickButton}</span>
        </OptionalTooltip>
      </Container>
    );
  }

  /**
   * Blank mode for when the value is null - the user can choose whether to enter picking or manually enter coordinates.
   *
   * This is not an ideal user experience, but is the best option for now as our formatted distance input doesn't
   *   support a null coordinate without an error state.
   *
   * This mode is more rare these days because many panels have forced picking now.
   */
  if (props.value == null) {
    return (
      <Container label={props.label}>
        {pickButton}

        <Box component="span" sx={{ ...((props.disabled || isPickingActive) && { color: 'text.disabled' }) }}>
          or
        </Box>

        <Button
          variant="outlined"
          disabled={props.disabled || isPickingActive}
          onClick={async () => {
            await dispatch(applyLayoutPointToTargetWithoutAdvancing(props.target, { x: 0, y: 0 }));
          }}
        >
          Define
        </Button>
      </Container>
    );
  }

  /**
   * Display / editing mode - the field has a valid point and is not currently picking. The user is allowed to edit
   *   the coordinate values unless this field is disabled.
   */
  return (
    <Container label={props.label}>
      <DistanceField
        canBeNull
        label="X Coordinate"
        value={props.value?.x ?? null}
        disabled={props.disabled}
        onSubmit={async (x) => {
          await dispatch(applyLayoutPointToTargetWithoutAdvancing(props.target, { x, y: props.value?.y ?? 0 }));
        }}
      />

      <DistanceField
        canBeNull
        label="Y Coordinate"
        value={props.value?.y ?? null}
        disabled={props.disabled}
        onSubmit={async (y) => {
          await dispatch(applyLayoutPointToTargetWithoutAdvancing(props.target, { x: props.value?.x ?? 0, y }));
        }}
      />

      {moveButton}
      {pickButton}

      {movementAnchor && (
        <PointMovementPopover
          anchor={{ element: movementAnchor }}
          onClose={() => setMovementAnchor(null)}
          onMove={async (movement) => {
            setMovementAnchor(null);

            if (props.value == null) return;
            await dispatch(
              applyLayoutPointToTargetWithoutAdvancing(props.target, movePointInDirection(props.value, movement)),
            );
          }}
        />
      )}
    </Container>
  );
}

// Not intended for external use; just separated out to keep the main picker field clean from the refs and effect here.
function EscapeModeControls(props: {
  label: string;
  disabled?: boolean;

  mode: EscapeModeSettings;

  onChange: (mode: EscapeModeSettings) => void;

  onEscapeCancelled: () => void;
  onEscapeTriggered: (point: Point2DInterface) => void;
}) {
  const { label, disabled, mode, onChange } = props;

  const xInputRef = useRef<HTMLInputElement>(null);
  const yInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mode.startCoordinate === 'x' && xInputRef.current) {
      xInputRef.current.focus();
    } else if (mode.startCoordinate === 'y' && yInputRef.current) {
      yInputRef.current.focus();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container label={label}>
      <DistanceField
        inputRef={xInputRef}
        label="X Coordinate"
        canBeNull
        value={mode.x ?? null}
        disabled={disabled}
        onSubmit={async (x, { finalizing }) => {
          if (finalizing && mode.y != null) {
            props.onEscapeTriggered({ x, y: mode.y });
          } else {
            onChange({ ...mode, x });
          }
        }}
      />

      <DistanceField
        inputRef={yInputRef}
        label="Y Coordinate"
        canBeNull
        value={mode.y ?? null}
        disabled={disabled}
        onSubmit={async (y, { finalizing }) => {
          if (finalizing && mode.x != null) {
            props.onEscapeTriggered({ x: mode.x, y });
          } else {
            onChange({ ...mode, y });
          }
        }}
      />

      <Button
        variant="text"
        disabled={disabled}
        onClick={() => {
          if (props.onEscapeCancelled) {
            props.onEscapeCancelled();
          }
        }}
      >
        Cancel
      </Button>

      <Button
        variant="outlined"
        disabled={disabled}
        onClick={async () => {
          if (mode.x != null && mode.y != null) {
            props.onEscapeTriggered({ x: mode.x, y: mode.y });
          }
        }}
      >
        Define
      </Button>
    </Container>
  );
}
